import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Code, Copy, Eye, Globe, User } from 'lucide-react';
import { useVisitors } from '@/hooks/useTracking';
import { Card, Button, EmptyState, PageHeader, Skeleton } from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 50;

const TRACKING_API_URL = 'https://sales-api.soliddigital.com';
const TRACKER_SCRIPT_URL = 'https://tracker.soliddigital.com/solid-sales-tracker.js';

const EMBED_SNIPPET = `<script src="${TRACKER_SCRIPT_URL}"
        data-api="${TRACKING_API_URL}/api/v1/tracking"></script>`;

const IDENTIFY_SNIPPET = `// Link a visitor to a contact by email (e.g. after form submission)
window.SolidSales.identify('prospect@example.com');

// Get the current visitor's ID
var visitorId = window.SolidSales.getVisitorId();

// Manually trigger a page view
window.SolidSales.track();`;

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="absolute top-2 right-2 p-1.5 rounded-md bg-grey-700 hover:bg-grey-600 text-grey-300 hover:text-white transition-colors"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

type VisitorFilter = 'all' | 'identified' | 'anonymous';

export default function Visitors() {
  const navigate = useNavigate();
  const [showSetup, setShowSetup] = useState(false);
  const [filter, setFilter] = useState<VisitorFilter>('all');
  const [page, setPage] = useState(0);

  const identifiedParam = filter === 'identified' ? true : filter === 'anonymous' ? false : undefined;
  const { data, isLoading } = useVisitors({
    take: PAGE_SIZE,
    skip: page * PAGE_SIZE,
    identified: identifiedParam,
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  const handleFilterChange = (newFilter: VisitorFilter) => {
    setFilter(newFilter);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Website Visitors"
        subtitle="Recent anonymous and identified visitors from your website"
        className="mb-0"
        actions={
          <button
            type="button"
            onClick={() => setShowSetup(!showSetup)}
            className="flex items-center gap-1.5 text-sm text-grey-500 hover:text-accent transition-colors"
          >
            <Code className="w-4 h-4" />
            Install Tracking Code
            {showSetup ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        }
      />

      {showSetup && (
        <Card padding="md">
          <h2 className="text-sm font-semibold text-secondary mb-3">Install Tracking Code</h2>
          <p className="text-sm text-grey-500 mb-4">
            Add the following script tag to your website, just before the closing <code className="text-xs bg-grey-100 px-1 py-0.5 rounded">&lt;/head&gt;</code> or <code className="text-xs bg-grey-100 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag. This works on any website — WordPress, static HTML, React, etc.
          </p>

          <div className="relative">
            <pre className="bg-grey-800 text-grey-100 text-xs rounded-lg p-4 pr-10 overflow-x-auto">
              <code>{EMBED_SNIPPET}</code>
            </pre>
            <CopyButton text={EMBED_SNIPPET} />
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-grey-50 rounded-lg p-3">
              <p className="text-xs font-medium text-secondary mb-1">Page Views</p>
              <p className="text-xs text-grey-500">Automatically tracks every page visit, referrer, and UTM parameters.</p>
            </div>
            <div className="bg-grey-50 rounded-lg p-3">
              <p className="text-xs font-medium text-secondary mb-1">Visitor Identity</p>
              <p className="text-xs text-grey-500">Uses a first-party cookie to recognize returning visitors across sessions.</p>
            </div>
            <div className="bg-grey-50 rounded-lg p-3">
              <p className="text-xs font-medium text-secondary mb-1">Email Linking</p>
              <p className="text-xs text-grey-500">Call the JS API after form submissions to link a visitor to their CRM contact.</p>
            </div>
          </div>

          <details className="mt-4">
            <summary className="text-xs font-medium text-grey-500 cursor-pointer hover:text-accent transition-colors">
              JavaScript API Reference
            </summary>
            <div className="relative mt-2">
              <pre className="bg-grey-800 text-grey-100 text-xs rounded-lg p-4 pr-10 overflow-x-auto">
                <code>{IDENTIFY_SNIPPET}</code>
              </pre>
              <CopyButton text={IDENTIFY_SNIPPET} />
            </div>
          </details>
        </Card>
      )}

      <Card padding="md">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-lg" />
            ))}
          </div>
        ) : !data?.data.length && filter === 'all' ? (
          <EmptyState
            icon={<Globe />}
            title="No visitor data yet"
            description="Embed the tracking script on your website to start collecting data"
          />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-grey-500">
                {data?.total ?? 0} {filter === 'identified' ? 'identified' : filter === 'anonymous' ? 'anonymous' : 'total'} visitors
              </div>
              <div className="flex items-center gap-1 bg-grey-100 rounded-lg p-0.5">
                {([
                  { key: 'all', label: 'All' },
                  { key: 'identified', label: 'Identified' },
                  { key: 'anonymous', label: 'Anonymous' },
                ] as const).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => handleFilterChange(opt.key)}
                    className={cn(
                      'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                      filter === opt.key
                        ? 'bg-white text-secondary shadow-sm'
                        : 'text-grey-500 hover:text-secondary',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {data?.data.length === 0 ? (
              <div className="text-center py-12">
                <User className="w-10 h-10 text-grey-300 mx-auto mb-3" />
                <p className="text-sm text-grey-500">
                  No {filter === 'identified' ? 'identified' : 'anonymous'} visitors found
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-grey-100">
                        <th className="text-left text-xs font-medium text-grey-500 uppercase tracking-wider pb-3 pr-4">
                          Visitor
                        </th>
                        <th className="text-left text-xs font-medium text-grey-500 uppercase tracking-wider pb-3 pr-4">
                          Pages
                        </th>
                        <th className="text-left text-xs font-medium text-grey-500 uppercase tracking-wider pb-3 pr-4">
                          Sessions
                        </th>
                        <th className="text-left text-xs font-medium text-grey-500 uppercase tracking-wider pb-3 pr-4">
                          First Seen
                        </th>
                        <th className="text-left text-xs font-medium text-grey-500 uppercase tracking-wider pb-3">
                          Last Seen
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-grey-50">
                      {data?.data.map((visitor) => (
                        <tr
                          key={visitor.visitorId}
                          className="hover:bg-grey-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/admin/visitors/${visitor.visitorId}`)}
                        >
                          <td className="py-3 pr-4">
                            <Link
                              to={`/admin/visitors/${visitor.visitorId}`}
                              className="flex items-center gap-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  visitor.contact ? 'bg-accent/10' : 'bg-grey-200'
                                }`}
                              >
                                {visitor.contact ? (
                                  <User className="w-4 h-4 text-accent" />
                                ) : (
                                  <Eye className="w-4 h-4 text-grey-400" />
                                )}
                              </div>
                              <div>
                                {visitor.contact ? (
                                  <span className="text-sm font-medium text-accent hover:underline">
                                    {visitor.contact.firstName} {visitor.contact.lastName}
                                  </span>
                                ) : (
                                  <span className="text-sm text-grey-600 font-mono">
                                    {visitor.visitorId.slice(0, 16)}...
                                  </span>
                                )}
                                {visitor.contact?.email && (
                                  <p className="text-xs text-grey-400">{visitor.contact.email}</p>
                                )}
                              </div>
                            </Link>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge variant="default" size="sm">{visitor.totalViews} pages</Badge>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-sm text-grey-600">{visitor.totalSessions}</span>
                          </td>
                          <td className="py-3 pr-4">
                            <span className="text-xs text-grey-500">
                              {new Date(visitor.firstSeen).toLocaleDateString()}
                            </span>
                          </td>
                          <td className="py-3">
                            <span className="text-xs text-grey-500">
                              {new Date(visitor.lastSeen).toLocaleString()}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-grey-100">
                    <p className="text-xs text-grey-500">
                      Page {page + 1} of {totalPages}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1}
                        aria-label="Next page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
